import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { RelatorioCandidaturasFiltro } from '../models/relatorio-candidaturas-filtro.model';
import { environment } from '@environments/environment';
import { DocumentoItem, FiltroDocumentos, OpcaoVaga } from '../models/documento.model';

@Injectable({
    providedIn: 'root'
})
export class DocumentosService {
    private readonly http = inject(HttpClient);
    /** URL base da API de Concurso DRH (https://bdp-concurso-api.dev.olualinet.ao/api/v1) */
    private readonly apiConcursoUrl = (environment.apiConcursoUrl || 'https://bdp-concurso-api.dev.olualinet.ao/api/v1').replace(/\/+$/, '');

    /**
     * Constrói os HttpParams comuns a partir do filtro.
     */
    private montarParametrosFiltro(filtro?: RelatorioCandidaturasFiltro): HttpParams {
        let params = new HttpParams();

        if (!filtro) {
            return params;
        }

        if (filtro.dataInicio) {
            const dataInicioStr = filtro.dataInicio instanceof Date
                ? filtro.dataInicio.toISOString()
                : filtro.dataInicio;
            params = params.set('dataInicio', dataInicioStr);
        }

        if (filtro.dataFim) {
            const dataFimStr = filtro.dataFim instanceof Date
                ? filtro.dataFim.toISOString()
                : filtro.dataFim;
            params = params.set('dataFim', dataFimStr);
        }

        if (filtro.concursoId) {
            params = params.set('concursoId', filtro.concursoId);
        }

        if (filtro.ofertaId) {
            params = params.set('ofertaId', filtro.ofertaId);
        }

        return params;
    }

    /**
     * Descarrega o relatório de candidaturas em formato PDF.
     * Rota Swagger: GET /api/v1/documentos/relatorio-candidaturas/pdf
     */
    obterRelatorioCandidaturasPdf(filtro?: RelatorioCandidaturasFiltro): Observable<Blob> {
        const params = this.montarParametrosFiltro(filtro);
        return this.http.get(`${this.apiConcursoUrl}/documentos/relatorio-candidaturas/pdf`, {
            params,
            responseType: 'blob'
        });
    }

    /**
     * Descarrega o relatório de candidaturas em formato Word (.docx).
     * Rota Swagger: GET /api/v1/documentos/relatorio-candidaturas/word
     */
    obterRelatorioCandidaturasWord(filtro?: RelatorioCandidaturasFiltro): Observable<Blob> {
        const params = this.montarParametrosFiltro(filtro);
        return this.http.get(`${this.apiConcursoUrl}/documentos/relatorio-candidaturas/word`, {
            params,
            responseType: 'blob'
        });
    }

    /**
     * Obtém a lista de documentos das candidaturas a partir da API de Concurso DRH.
     * Rota Swagger: GET /api/v1/candidatura-documentos
     */
    listarDocumentos(filtro?: FiltroDocumentos): Observable<any> {
        let params = new HttpParams();

        if (filtro) {
            if (filtro.pageNumber) {
                params = params.set('PageNumber', filtro.pageNumber.toString());
            }
            if (filtro.pageSize) {
                params = params.set('PageSize', filtro.pageSize.toString());
            }
            if (filtro.pesquisa) {
                params = params.set('SearchTerm', filtro.pesquisa);
            }
            if (filtro.concursoId) {
                params = params.set('ConcursoId', filtro.concursoId);
            }
            if (filtro.dataInicio) {
                const dataInicioStr = filtro.dataInicio instanceof Date
                    ? filtro.dataInicio.toISOString()
                    : filtro.dataInicio;
                params = params.set('CreatedAt', dataInicioStr);
            }
        }

        return this.http.get<any>(`${this.apiConcursoUrl}/candidatura-documentos`, { params }).pipe(
            map((res: any) => {
                if (res && Array.isArray(res.data)) {
                    return {
                        ...res,
                        data: res.data.map((item: any) => this.normalizarDocumento(item))
                    };
                }
                if (Array.isArray(res)) {
                    return res.map((item: any) => this.normalizarDocumento(item));
                }
                return res;
            })
        );
    }

    /**
     * Converte o DTO da API (CandidaturaDocumentoDto) para a estrutura visual da tabela
     */
    private normalizarDocumento(item: any): DocumentoItem {
        const doc = item?.candidatoDocumento;
        const anexo = doc?.anexo;
        const tipo = doc?.tipoDocumento;
        const candidato = item?.candidato;

        const nomeFicheiro = anexo?.nomeFicheiro || '';
        const formato = nomeFicheiro.split('.').pop()?.toUpperCase() || (anexo?.mimeType ? anexo.mimeType.split('/').pop()?.toUpperCase() : 'PDF') || 'PDF';
        const tamanhoKb = doc?.tamanhoBytes ? Math.round(doc.tamanhoBytes / 1024) : (anexo?.tamanho ? Math.round(anexo.tamanho / 1024) : undefined);

        return {
            id: item?.id || doc?.id || '',
            titulo: tipo?.designacao || nomeFicheiro || 'Documento de Candidatura',
            tipoPublico: 'PROFISSIONAL',
            vagaNome: candidato?.nome ? `${candidato.nome}${candidato.bi ? ' (' + candidato.bi + ')' : ''}` : 'Candidatura',
            concursoId: item?.concursoId,
            formato: formato,
            tamanhoKb: tamanhoKb,
            dataCriacao: item?.createdAt || doc?.createdAt || new Date().toISOString(),
            urlDownload: anexo?.linkAnexoPreview || (doc?.anexoId ? `${this.apiConcursoUrl}/anexos/${doc.anexoId}/preview` : undefined)
        };
    }

    /**
     * Lista as vagas disponíveis para preencher o dropdown de filtro (API de Profissionais)
     */
    listarVagas(): Observable<OpcaoVaga[]> {
        return this.http.get<OpcaoVaga[]>(`${environment.apiUrl}/profissionais/getAllVaga`);
    }

    /**
     * Descarrega o documento individual por ID (Preview ou Download do Anexo)
     */
    descarregarDocumento(documentoId: string): Observable<Blob> {
        return this.http.get(`${this.apiConcursoUrl}/anexos/${documentoId}/preview`, {
            responseType: 'blob'
        });
    }

}